import {
  Alert,
  Anchor,
  Button,
  Card,
  Divider,
  Group,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useSignIn } from '@clerk/clerk-react';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBrandFacebook,
  IconBrandGithub,
  IconBrandGoogle,
} from '@tabler/icons-react';
import { useState, type FormEvent } from 'react';
import { clerkError } from './clerkErrors.js';
import { useNavigate } from 'react-router-dom';

/**
 * Our own sign-in, driven by Clerk's headless hooks.
 *
 * Two steps rather than one, and not for style: an account created through
 * Google has no password, and asking Clerk to verify one returns "the
 * verification strategy is not valid for this account". So we identify the
 * account first, ask Clerk which methods it actually supports, and show only
 * those. A single form that always asks for a password is wrong for every
 * social-created account.
 */

type Social = 'oauth_google' | 'oauth_github' | 'oauth_facebook';

const SOCIALS: { strategy: Social; label: string; Icon: typeof IconBrandGoogle }[] = [
  { strategy: 'oauth_google', label: 'Google', Icon: IconBrandGoogle },
  { strategy: 'oauth_github', label: 'GitHub', Icon: IconBrandGithub },
  { strategy: 'oauth_facebook', label: 'Facebook', Icon: IconBrandFacebook },
];

type Step = 'identify' | 'password' | 'code';

export function ClerkSignInForm() {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('identify');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [factors, setFactors] = useState<string[]>([]);
  const [emailAddressId, setEmailAddressId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState<Social | null>(null);

  async function finish(createdSessionId: string | null) {
    await setActive?.({ session: createdSessionId });
    navigate('/app', { replace: true });
  }

  /** Step one: find out how this account is allowed to sign in. */
  async function identify(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setBusy(true);
    setError('');
    try {
      const attempt = await signIn.create({ identifier: email.trim() });

      const supported = (attempt.supportedFirstFactors ?? []).map((f) => f.strategy);
      setFactors(supported);

      const emailFactor = (attempt.supportedFirstFactors ?? []).find(
        (f) => f.strategy === 'email_code',
      ) as { emailAddressId?: string } | undefined;
      setEmailAddressId(emailFactor?.emailAddressId ?? null);

      if (supported.includes('password')) {
        setStep('password');
        return;
      }
      if (emailFactor?.emailAddressId) {
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: emailFactor.emailAddressId,
        });
        setStep('code');
        return;
      }

      // Neither password nor a code: the account is social-only.
      setError(
        'This account signs in with a social provider. Use one of the buttons above.',
      );
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setBusy(true);
    setError('');
    try {
      const attempt = await signIn.attemptFirstFactor({ strategy: 'password', password });
      if (attempt.status === 'complete') {
        await finish(attempt.createdSessionId);
        return;
      }
      setError(`This account needs a further step (${attempt.status}) that is not handled here yet.`);
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!signIn) return;
    setBusy(true);
    setError('');
    try {
      const attempt = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
      if (attempt.status === 'complete') {
        await finish(attempt.createdSessionId);
        return;
      }
      setError(`This account needs a further step (${attempt.status}) that is not handled here yet.`);
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode() {
    if (!signIn || !emailAddressId) return;
    setBusy(true);
    setError('');
    try {
      await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId });
      setStep('code');
    } catch (err) {
      setError(clerkError(err));
    } finally {
      setBusy(false);
    }
  }

  async function social(strategy: Social) {
    if (!isLoaded || !signIn) return;
    setSocialBusy(strategy);
    setError('');
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/login/sso-callback',
        redirectUrlComplete: '/app',
      });
    } catch (err) {
      setError(clerkError(err));
      setSocialBusy(null);
    }
  }

  function restart() {
    setStep('identify');
    setPassword('');
    setCode('');
    setError('');
  }

  const errorBlock = error && (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
      <Text size="sm">{error}</Text>
    </Alert>
  );

  // ---------------------------------------------------------------- step 2
  if (step !== 'identify') {
    return (
      <Card shadow="sm">
        <form onSubmit={step === 'password' ? submitPassword : submitCode}>
          <Stack>
            <Group gap={6}>
              <Anchor component="button" type="button" size="sm" onClick={restart}>
                <Group gap={4}>
                  <IconArrowLeft size={13} />
                  Back
                </Group>
              </Anchor>
              <Text size="sm" c="dimmed" truncate>
                · {email}
              </Text>
            </Group>

            {step === 'password' ? (
              <PasswordInput
                label="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                data-autofocus
                required
              />
            ) : (
              <Stack gap={6}>
                <Text size="sm" fw={500}>
                  Enter the code we emailed you
                </Text>
                <PinInput length={6} value={code} onChange={setCode} oneTimeCode type="number" />
              </Stack>
            )}

            {errorBlock}

            <Button
              type="submit"
              loading={busy}
              fullWidth
              disabled={step === 'code' && code.length < 6}
            >
              Sign in
            </Button>

            {/* Offered only when Clerk said this account supports it. */}
            {step === 'password' && factors.includes('email_code') && emailAddressId && (
              <Anchor component="button" type="button" size="sm" ta="center" onClick={() => void sendCode()}>
                Email me a code instead
              </Anchor>
            )}
          </Stack>
        </form>
      </Card>
    );
  }

  // ---------------------------------------------------------------- step 1
  return (
    <Card shadow="sm">
      <form onSubmit={identify}>
        <Stack>
          <Group grow>
            {SOCIALS.map(({ strategy, label, Icon }) => (
              <Button
                key={strategy}
                variant="default"
                leftSection={<Icon size={16} />}
                loading={socialBusy === strategy}
                disabled={socialBusy !== null && socialBusy !== strategy}
                onClick={() => void social(strategy)}
                type="button"
              >
                {label}
              </Button>
            ))}
          </Group>

          <Divider label="or continue with email" labelPosition="center" />

          <TextInput
            label="Email"
            placeholder="you@agency.com"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
          />

          {errorBlock}

          <Button type="submit" loading={busy} disabled={!isLoaded} fullWidth>
            Continue
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            Access is by invitation. If you sign in and are told you have no account, ask an
            administrator to add you.
          </Text>
        </Stack>
      </form>
    </Card>
  );
}
